import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Divider, Form, Input, Select, Slider, Space, Spin, Tag, Timeline, message } from 'antd';
import {
  ApiOutlined,
  CheckCircleOutlined,
  ExperimentOutlined,
  PlayCircleOutlined,
  SaveOutlined,
  SoundOutlined,
} from '@ant-design/icons';
import { get, post, put } from '../../api/request';
import { API_ENDPOINTS } from '../../api/config';
import type { AISettingType, AITtsTestResultType } from '../AIManagement/types';
import HlsMedia from '../../components/HlsMedia';
import './CoreTestManagement.css';

const { TextArea } = Input;

const quickRouterModelOptions = [
  { label: 'speech-2.8-turbo（推荐，响应快）', value: 'speech-2.8-turbo' },
  { label: 'speech-2.8-hd（高质量）', value: 'speech-2.8-hd' },
  { label: 'speech-2.6-turbo（便宜对照）', value: 'speech-2.6-turbo' },
  { label: 'speech-2.6-hd（高质量对照）', value: 'speech-2.6-hd' },
  { label: 'speech-02-hd（兼容旧接口）', value: 'speech-02-hd' },
];

const quickRouterVoiceOptions = [
  { label: 'male-qn-qingse（清澈男声）', value: 'male-qn-qingse' },
  { label: 'female-shaonv（少女音色）', value: 'female-shaonv' },
  { label: 'female-yujie（御姐音色）', value: 'female-yujie' },
  { label: 'audiobook_male_1（男声有声书）', value: 'audiobook_male_1' },
  { label: 'audiobook_female_1（女声有声书）', value: 'audiobook_female_1' },
];

const sceneOptions = [
  { key: 'sleep', title: '晚安哄睡', desc: '柔声、低速、雨声或白噪，重点看自然停顿和安抚感。' },
  { key: 'wake', title: '起床铃声', desc: '清晰、渐强、短句播报，重点看唤醒但不刺耳。' },
  { key: 'male', title: '男声陪伴', desc: '低频、稳定、短回应，重点看压力释放和边界感。' },
  { key: 'female', title: '女声陪伴', desc: '轻故事、柔和、少打扰，重点看情绪表达。' },
];

const quickRouterVoices = [
  { key: 'male-qn-qingse', title: '清澈男声', desc: '适合陪伴短句、晨间提示和温和对话。' },
  { key: 'female-shaonv', title: '柔亮女声', desc: '适合晚安、轻故事和情绪陪伴。' },
  { key: 'audiobook_male_1', title: '男声有声书', desc: '适合长段朗读、故事和内容播报。' },
  { key: 'audiobook_female_1', title: '女声有声书', desc: '适合睡前故事、轻内容和安抚朗读。' },
];

const commonChecklist = [
  ['供应商鉴权', '验证 key、appid、token、区域配置'],
  ['音色列表', '拉取可用音色并筛选候选样本'],
  ['同步生成', '文本一次性生成音频并可播放'],
  ['流式播放', '边生成边播放，验证首字延迟'],
  ['音频缓存', '相同文本和参数复用音频文件'],
  ['失败兜底', '失败后记录错误并切换备用方案'],
];

const waveBars = [34, 58, 42, 76, 50, 68, 36, 82, 48, 70, 44, 62, 38, 74, 52, 60];
const sliderConfigs = [
  { key: 'speed', label: '语速', valueKey: 'speed', min: 50, max: 200, tip: '慢一点更适合哄睡，快一点适合晨间播报' },
  { key: 'pitch', label: '音高', valueKey: 'pitch', min: 0, max: 100, tip: '保持中性，避免过尖或过低造成疲劳' },
  { key: 'emotion', label: '情绪强度', valueKey: 'emotion', min: 0, max: 100, tip: '控制安抚感、亲近感和故事感' },
  { key: 'background', label: '背景声比例', valueKey: 'background', min: 0, max: 100, tip: '只做陪衬，不能压过人声' },
] as const;

const providerConfigs = {
  name: 'QuickRouter',
  title: 'QuickRouter 测试管理',
  subtitle: '使用现有聚合平台验证 TTS、音色、播放链路、成本和音色复刻可行性。',
  tag: '已打通',
  color: 'blue',
  defaultVoice: 'male-qn-qingse',
  voices: quickRouterVoices,
  timeline: [
    { dot: <CheckCircleOutlined />, color: 'green', children: '固定测试文本、场景、评分维度。' },
    { dot: <SoundOutlined />, color: 'blue', children: '跑通 QuickRouter / MiniMax TTS 样本。' },
    { color: 'blue', children: '筛选适合声屿的中文音色并同步到音色管理。' },
    { color: 'gray', children: '记录成本、延迟和失败率，作为生产链路依据。' },
  ],
};

interface BackgroundMusicOption {
  id: number;
  title: string;
  category: string;
  audio_url: string;
  volume: number;
  status: number;
}

const CoreTestManagement: React.FC = () => {
  const config = providerConfigs;
  const [form] = Form.useForm();
  const [setting, setSetting] = useState<AISettingType | null>(null);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState<AITtsTestResultType | null>(null);
  const [backgroundMusic, setBackgroundMusic] = useState<BackgroundMusicOption[]>([]);
  const [selectedBackgroundId, setSelectedBackgroundId] = useState<number | null>(null);
  const [scene, setScene] = useState('sleep');
  const [voice, setVoice] = useState(config.defaultVoice);
  const [model, setModel] = useState('speech-2.8-turbo');
  const [speed, setSpeed] = useState(92);
  const [pitch, setPitch] = useState(50);
  const [emotion, setEmotion] = useState(68);
  const [background, setBackground] = useState(32);
  const [text, setText] = useState('今晚辛苦了。你不用马上睡着，只需要把肩膀放松一点。我会把雨声放得更远，让故事慢慢变轻。');

  const selectedScene = useMemo(
    () => sceneOptions.find((item) => item.key === scene) || sceneOptions[0],
    [scene]
  );
  const selectedVoice = useMemo(
    () => config.voices.find((item) => item.key === voice) || config.voices[0],
    [config.voices, voice]
  );

  const loadConfig = useCallback(async () => {
    setLoadingConfig(true);
    try {
      const data = await get<AISettingType>(API_ENDPOINTS.ai_management.config);
      setSetting(data);
      setModel(data.quickrouter_tts_model || 'speech-2.8-turbo');
      setVoice(data.quickrouter_tts_voice_id || config.defaultVoice);
      form.setFieldsValue({
        quickrouter_tts_model: data.quickrouter_tts_model || 'speech-2.8-turbo',
        quickrouter_tts_voice_id: data.quickrouter_tts_voice_id || 'male-qn-qingse',
        quickrouter_tts_language_boost: data.quickrouter_tts_language_boost || 'Chinese',
      });
    } catch (error) {
      message.error(error instanceof Error ? error.message : '加载语音配置失败');
    } finally {
      setLoadingConfig(false);
    }
  }, [config.defaultVoice, form]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const loadBackgroundMusic = useCallback(async () => {
    try {
      const data = await get<BackgroundMusicOption[]>(`${API_ENDPOINTS.storage.backgroundMusic}?status=1`);
      setBackgroundMusic(data);
      if (!selectedBackgroundId && data.length > 0) {
        setSelectedBackgroundId(data[0].id);
      }
    } catch (error) {
      message.error(error instanceof Error ? error.message : '加载背景音乐库失败');
    }
  }, [selectedBackgroundId]);

  useEffect(() => {
    loadBackgroundMusic();
  }, [loadBackgroundMusic]);

  const handleSaveConfig = async () => {
    const values = await form.validateFields();
    setSavingConfig(true);
    try {
      await put(API_ENDPOINTS.ai_management.config, values);
      message.success('语音配置已保存');
      await loadConfig();
    } catch (error) {
      message.error(error instanceof Error ? error.message : '保存语音配置失败');
    } finally {
      setSavingConfig(false);
    }
  };

  const handleTtsTest = async () => {
    setTesting(true);
    setResult(null);
    try {
      const data = await post<AITtsTestResultType>(API_ENDPOINTS.ai_management.testTts, {
        provider: 'quickrouter',
        text,
        model,
        voice,
        speed: speed / 100,
        pitch: Math.round((pitch - 50) / 5),
        volume: 1,
        emotion: String(emotion),
      });
      setResult(data);
      message.success('语音生成成功');
    } catch (error) {
      message.error(error instanceof Error ? error.message : '语音生成失败');
    } finally {
      setTesting(false);
    }
  };

  const audioSrc = result?.audio_url || (result?.audio_base64 ? `data:audio/${result.audio_format || 'mp3'};base64,${result.audio_base64}` : '');
  const selectedBackground = useMemo(
    () => backgroundMusic.find((item) => item.id === selectedBackgroundId),
    [backgroundMusic, selectedBackgroundId]
  );

  return (
    <div className="core-test-page">
      <div className="core-test-header">
        <div>
          <Space wrap style={{ marginBottom: 8 }}>
            <Tag color={config.color}>{config.tag}</Tag>
            <Tag color="default">{config.name}</Tag>
          </Space>
          <h1 className="page-title">{config.title}</h1>
          <p className="core-test-subtitle">{config.subtitle}</p>
        </div>
        <div className="core-test-actions">
          <Button icon={<ApiOutlined />}>接口草案</Button>
          <Button type="primary" icon={<ExperimentOutlined />} loading={testing} onClick={handleTtsTest}>
            生成试听
          </Button>
        </div>
      </div>

      <div className="core-test-layout">
        <div>
          <Card bordered={false} className="core-test-panel" title="平台配置">
            <Spin spinning={loadingConfig}>
              <Form form={form} layout="vertical">
                <div className="core-test-form-grid">
                  <Form.Item label="MiniMax 语音模型" name="quickrouter_tts_model" rules={[{ required: true, message: '请选择语音模型' }]}>
                    <Select options={quickRouterModelOptions} onChange={(value) => setModel(value)} />
                  </Form.Item>
                  <Form.Item label="MiniMax voice_id" name="quickrouter_tts_voice_id" rules={[{ required: true, message: '请选择或输入音色' }]}>
                    <Select options={quickRouterVoiceOptions} onChange={(value) => setVoice(value)} />
                  </Form.Item>
                  <Form.Item label="语言增强" name="quickrouter_tts_language_boost">
                    <Input placeholder="Chinese" />
                  </Form.Item>
                  <div className="core-test-config-status">
                    <Tag color={setting?.has_api_key ? 'green' : 'red'}>
                      QuickRouter Key: {setting?.has_api_key ? '已保存' : '未保存'}
                    </Tag>
                  </div>
                </div>
                <Button icon={<SaveOutlined />} loading={savingConfig} onClick={handleSaveConfig}>
                  保存语音配置
                </Button>
              </Form>
            </Spin>
          </Card>

          <Card bordered={false} className="core-test-panel" title="测试配置" style={{ marginTop: 16 }}>
            <div className="core-test-form-grid">
              <div>
                <div className="form-section-title">测试平台</div>
                <div className="core-test-provider-fixed">
                  <strong>{config.name}</strong>
                  <Tag color={config.color}>{config.tag}</Tag>
                </div>
              </div>
              <div>
                <div className="form-section-title">背景声模板</div>
                <Select
                  value={selectedBackgroundId}
                  options={backgroundMusic.map((item) => ({
                    label: item.title,
                    value: item.id,
                  }))}
                  onChange={setSelectedBackgroundId}
                  placeholder="请选择背景音乐"
                  style={{ width: '100%' }}
                />
                {selectedBackground && (
                  <HlsMedia className="core-test-background-audio" src={selectedBackground.audio_url} controls preload="none" />
                )}
              </div>
            </div>

            <Divider />

            <div className="form-section-title">声音场景</div>
            <div className="core-test-scene-grid">
              {sceneOptions.map((item) => (
                <button
                  key={item.key}
                  className={scene === item.key ? 'core-test-choice active' : 'core-test-choice'}
                  type="button"
                  onClick={() => setScene(item.key)}
                >
                  <strong>{item.title}</strong>
                  <span>{item.desc}</span>
                </button>
              ))}
            </div>

            <Divider />

            <div className="form-section-title">候选音色</div>
            <div className="core-test-voice-grid">
              {config.voices.map((item) => (
                <button
                  key={item.key}
                  className={voice === item.key ? 'core-test-choice active' : 'core-test-choice'}
                  type="button"
                  onClick={() => setVoice(item.key)}
                >
                  <strong>{item.title}</strong>
                  <span>{item.desc}</span>
                </button>
              ))}
            </div>

            <Divider />

            <div className="form-section-title">朗读文本</div>
            <TextArea value={text} onChange={(event) => setText(event.target.value)} rows={5} maxLength={800} />
          </Card>

          <Card bordered={false} className="core-test-panel" title="参数调试" style={{ marginTop: 16 }}>
            {sliderConfigs.map((item) => {
              const stateMap = {
                speed: [speed, setSpeed],
                pitch: [pitch, setPitch],
                emotion: [emotion, setEmotion],
                background: [background, setBackground],
              } as const;
              const [value, setter] = stateMap[item.valueKey];
              return (
              <div className="core-test-slider-row" key={item.key}>
                <div className="core-test-slider-meta">
                  <strong>{item.label}</strong>
                  <span>{value as number}% · {item.tip}</span>
                </div>
                <Slider min={item.min} max={item.max} value={value as number} onChange={setter as (value: number) => void} />
              </div>
            )})}
          </Card>
        </div>

        <div className="core-test-preview">
          <Card bordered={false} className="core-test-panel" title="试听预览">
            <div className="core-test-player">
              <Space wrap>
                <Tag color={config.color}>{config.name}</Tag>
                <Tag color="green">{model}</Tag>
                <Tag color="blue">{selectedScene.title}</Tag>
                <Tag color="magenta">{selectedVoice.title}</Tag>
              </Space>
              {audioSrc ? (
                <HlsMedia className="core-test-audio" src={audioSrc} controls />
              ) : (
                <div className="core-test-wave" aria-hidden="true">
                  {waveBars.map((height, index) => <span key={index} style={{ height }} />)}
                </div>
              )}
              <Button type="primary" icon={<PlayCircleOutlined />} block loading={testing} onClick={handleTtsTest}>
                真实试听
              </Button>
            </div>

            <div className="core-test-metrics" style={{ marginTop: 16 }}>
              <div className="core-test-metric">
                <strong>{result ? `${result.elapsed_ms}ms` : '-'}</strong>
                <span>生成耗时</span>
              </div>
              <div className="core-test-metric">
                <strong>{result?.audio_format || 'mp3'}</strong>
                <span>音频格式</span>
              </div>
              <div className="core-test-metric">
                <strong>{result ? '成功' : '待测'}</strong>
                <span>接口状态</span>
              </div>
            </div>
          </Card>

          <Card bordered={false} className="core-test-panel" title="测试清单">
            <div className="core-test-checklist">
              {commonChecklist.map(([title, desc], index) => (
                <div className="core-test-check" key={title}>
                  <div>
                    <strong>{title}</strong>
                    <span>{desc}</span>
                  </div>
                  <Tag color={index < 2 ? 'processing' : 'default'}>{index < 2 ? '先测' : '待接入'}</Tag>
                </div>
              ))}
            </div>
          </Card>

          <Card bordered={false} className="core-test-panel" title="落地节奏">
            <Timeline items={config.timeline} />
          </Card>
        </div>
      </div>
    </div>
  );
};

export const QuickRouterCoreTest: React.FC = () => <CoreTestManagement />;

export default QuickRouterCoreTest;
